import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectUnits } from '../../store/slices/settings/unitsSlice';
import {
  handleSetWindTemp,
  selectWindFactor,
} from '../../store/slices/settings/windFactorSlice';
import { fromInputTemp, toDisplayTemp } from '../../../helpers/helpers';
import { logger } from '../../../helpers/logger';
import { windThresholdWrites } from '../../../helpers/payloads/settingsPayloads';
import { saveFailedMessage, settingsMessage } from './settingsMessage';
import { sendSettingWrites } from './sendSettingWrites';
import { useSettingsDeviceIds } from './useSettingsDeviceIds';

const WIND_SPEEDS = ['low', 'med', 'high', 'extreme'];
const WIND_KEYS = ['lowWindTemp', 'medWindTemp', 'highWindTemp', 'extremeWindTemp'];

const windMessage = (theme, message) =>
  settingsMessage('wind factor trigger', theme, message);

/**
 * The confirm text for new wind-factor triggers: `saved` are the stored °C
 * triggers (low, med, high, extreme), `typed` the four input values in the
 * operator's unit.
 */
export const reviewWindFactor = ({ saved, isF, typed }) => {
  if (typed.some((value) => !value)) {
    return windMessage('input fields incomplete', [
      'input fields incomplete please field all the input fields.',
    ]);
  }

  const shownSaved = saved.map((celsius) => toDisplayTemp(+celsius, isF));
  const unit = isF ? '°f' : '°c';
  const changes = typed.map(Number).flatMap((newTemp, idx) =>
    !Number.isNaN(newTemp) && newTemp !== shownSaved[idx]
      ? [
          `you are about to set the ${WIND_SPEEDS[idx]} wind factor trigger temperature from ${shownSaved[idx]}${unit} to ${newTemp}${unit}.`,
        ]
      : [],
  );

  return changes.length === 0
    ? windMessage('input fields unchanged', [
        'input fields set to the same temperature as before.',
      ])
    : windMessage('change temperature', changes);
};

/**
 * The wind-factor panel: four trigger inputs. `review` builds the confirm
 * message; `confirm` sends the triggers to every device and stores them only
 * once the devices took them. It resolves to the failure message, or null.
 */
export const useSaveWindFactor = () => {
  const dispatch = useDispatch();
  const { isF } = useSelector(selectUnits);
  const windFactor = useSelector(selectWindFactor);
  const deviceIds = useSettingsDeviceIds();

  const lowWindTempRef = useRef(null);
  const medWindTempRef = useRef(null);
  const highWindTempRef = useRef(null);
  const extremeWindTempRef = useRef(null);
  const typedValues = () =>
    [lowWindTempRef, medWindTempRef, highWindTempRef, extremeWindTempRef].map(
      (ref) => ref.current.value,
    );

  const review = () =>
    reviewWindFactor({
      saved: WIND_KEYS.map((key) => windFactor[key]),
      isF,
      typed: typedValues(),
    });

  const confirm = async () => {
    const temps = typedValues().map((value) => fromInputTemp(value, isF));
    try {
      await sendSettingWrites(
        windThresholdWrites({ temps, deviceIds: deviceIds() }),
      );
    } catch (error) {
      logger.error('wind factor save failed', error);
      return saveFailedMessage(
        'wind factor trigger',
        'change temperature',
        error,
        { asList: true },
      );
    }
    WIND_KEYS.forEach((objectName, idx) =>
      dispatch(handleSetWindTemp({ objectName, temp: temps[idx] })),
    );
    return null;
  };

  return {
    panelProps: {
      lowWindTempRef,
      medWindTempRef,
      highWindTempRef,
      extremeWindTempRef,
    },
    review,
    confirm,
  };
};
